import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Button } from "../../../../components/ui";
import localization from "../../../../config/localization";
import verificationService from "../../service/verification.service";
import {
  RetrieveUserBankAccountResponse,
  UserBankAccountDetail,
} from "../../models/response/retrieveUserBankAccountResponse";
import { BankAccountStatus } from "../../../../models/enums/bankAccountStatus";
import { ConfirmationModal } from "../../../../components/ui/modals/ConfirmationModal";
import { toastService as toast } from "../../../../components/ui/toast/toast.service";
import { userPendingStore } from "../../../users/store/userPendingData.store";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { changeLoaderState } from "../../../../reducers/appStateReducer";
import type { RootState } from "../../../../store/store";
import { useRealTimeStore } from "../../../../store/signalrRealTime.store";

export function BankAccountList({
  handleAddButton,
  handleEditButton,
  handleViewButton,
}: {
  handleAddButton: () => void;
  handleEditButton: (bankAccount: UserBankAccountDetail) => void;
  handleViewButton: (bankAccount: UserBankAccountDetail) => void;
}) {
  const [bankAccounts, setBankAccounts] =
    useState<RetrieveUserBankAccountResponse | null>(null);
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state: RootState) => state.appState.showLoader);
  const latestBankUpdate = useRealTimeStore((state) => state.latestBankUpdate);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedBankAccountId, setSelectedBankAccountId] =
    useState<string>("");
  const fetchBankAccounts = async () => {
    const response = await verificationService.retrieveBankAccounts();
    if (response.isSuccess) {
      setBankAccounts(response.data);
    }
    dispatch(changeLoaderState(false));
  };

  useEffect(() => {
    dispatch(changeLoaderState(true));
    fetchBankAccounts();
  }, []);

  useEffect(() => {
    if (!latestBankUpdate) return;
    if (!bankAccounts?.details?.length) return;

    const latestRemarks =
      (latestBankUpdate as any).Remarks ??
      (latestBankUpdate as any).remarks ??
      null;

    setBankAccounts((prev) => {
      if (!prev?.details?.length) return prev;

      const index = prev.details.findIndex(
        (item) => item.id === latestBankUpdate.id,
      );
      if (index === -1) return prev;

      const updatedDetails = [...prev.details];
      updatedDetails[index] = {
        ...updatedDetails[index],
        status: latestBankUpdate.status ?? updatedDetails[index].status,
        remarks: latestRemarks ?? updatedDetails[index].remarks,
      };

      return {
        ...prev,
        details: updatedDetails,
      };
    });
  }, [latestBankUpdate, bankAccounts?.details?.length]);

  const getStatusClasses = (status: string) => {
    switch (status.toLowerCase()) {
      case BankAccountStatus.Approved.toLowerCase():
        return "bg-[#003525] text-[#26FFBD]";
      case BankAccountStatus.Pending.toLowerCase():
        return "bg-[#2B2700] text-[#DDCD32]";
      case BankAccountStatus.Rejected.toLowerCase():
        return "bg-[#280401] text-[#FF6355]";
      default:
        return "bg-gray-500 text-white";
    }
  };


  const handleDeleteBankAccount = async (id: string) => {
    var response = await verificationService.deleteBankAccount(id);
    if (response.isSuccess) {
      userPendingStore.getState().fetchPendingData();
      toast.success(localization.bank_deletion_success);
      fetchBankAccounts();
    }
    setIsModalOpen(false);
  };

  if (isLoading) {
    return null;
  }

  let content = null;

  if (!bankAccounts || !bankAccounts.details || bankAccounts.details.length === 0) {
    content = (
      <div className="flex flex-col items-center justify-center h-full text-center">

        <div className="flex items-center justify-center mb-6">
          <img
            src={"/images/img-banking.svg"}
            alt="no bank data"
          />
        </div>

        <h1 className="text-white text-xl font-semibold mb-2">
          {localization.no_bank_details}
        </h1>

        <Button
          type="submit"
          className="mt-2 md:w-1/2 w-full mx-auto py-4 rounded-md text-white bg-primary-gradient"
          onClick={handleAddButton}
        >
          <div className="flex items-center justify-center gap-2">
            <FontAwesomeIcon icon={faPlus} />
            <span>{localization.add_bank_account}</span>
          </div>
        </Button>
      </div>
    )
  }

  else {
    content = (
      <div>
        <div className="flex justify-end mb-4">
          {bankAccounts && bankAccounts.details.length < 3 && (
            <Button className="mt-2 py-2 px-4 rounded-md text-white bg-primary-gradient flex items-center justify-center gap-2" onClick={handleAddButton}>
              <FontAwesomeIcon icon={faPlus} />
              <span>{localization.add_bank_account}</span>
            </Button>
          )}
        </div>

        <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-6 items-start">
          {bankAccounts.details.map((bankAccount) => (
            <div
              key={bankAccount.id}
              className="w-full bg-black border border-[#333333] rounded-md p-4 mt-3"
            >
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1 min-[400px]:flex-row min-[400px]:items-center min-[400px]:justify-between min-[400px]:gap-4">
                  <p className="text-[#D0D0D0] text-lg leading-5 shrink-0">
                    {localization.account_number1}
                  </p>
                  <div className="min-w-0 w-full min-[400px]:w-auto rounded-sm">
                    <p className="text-white font-semibold text-lg leading-5 font-mono tabular-nums whitespace-nowrap min-[400px]:text-right">
                      {bankAccount.accountnumber}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="text-[#D0D0D0] text-[14px] leading-5">
                      {localization.status}
                    </p>
                    <span
                      className={`px-2 py-1 rounded text-xs font-extrabold capitalize ${getStatusClasses(
                        bankAccount.status
                      )}`}
                    >
                      {bankAccount.status.toLowerCase()}
                    </span>
                  </div>

                  <div className="flex gap-2 shrink-0">
                    <Button
                      className="w-8 h-8 flex items-center justify-center border border-[#2a2a2a] rounded-md text-[#D0D0D0] hover:text-white"
                      onClick={() => handleViewButton(bankAccount)}
                    >
                      <img
                        src={"/icons/ic-eye.svg"}
                        alt="eye"
                        className="w-5 h-5"
                      />
                    </Button>

                    {bankAccount.status.toLowerCase() !==
                      BankAccountStatus.Approved.toLowerCase() && (
                        <>
                          <Button
                            className="w-8 h-8 flex items-center justify-center border border-[#2a2a2a] rounded-md text-[#D0D0D0] hover:text-white"
                            onClick={() => handleEditButton(bankAccount)}
                          >
                            <img
                              src={"/icons/ic-edit.svg"}
                              alt="edit"
                              className="w-5 h-5"
                            />
                          </Button>

                          <Button
                            className="w-8 h-8 flex items-center justify-center border border-[#2a2a2a] rounded-md text-[#D0D0D0] hover:text-red-500"
                            onClick={() => {
                              setIsModalOpen(true);
                              setSelectedBankAccountId(bankAccount.id);
                            }}
                          >
                            <img
                              src={"/icons/ic-trash.svg"}
                              alt="trash"
                              className="w-5 h-5"
                            />
                          </Button>
                        </>
                      )}
                  </div>
                </div>
              </div>

              {bankAccount.status.toLowerCase() ===
                BankAccountStatus.Rejected.toLowerCase() &&
                bankAccount.remarks && (
                  <div className="mt-1 pt-3 flex items-start justify-between gap-4">
                    <p className="text-gray-400 text-sm min-w-[90px] md:min-w-[110px] pt-0.5">
                      {localization.remark}
                    </p>

                    <p className="text-white text-sm md:text-[14px] leading-5 text-right break-words whitespace-pre-wrap flex-1">
                      {bankAccount.remarks}
                    </p>
                  </div>
                )}
            </div>
          ))}
        </div>
        {isModalOpen && (
          <ConfirmationModal
            isOpen={isModalOpen}
            title={localization.delete_bank_confirmation}
            icon={"/icons/ic-error.svg"}
            blurColor="bg-[#FFD4D4]"
            confirmText="Yes"
            cancelText="No"
            onConfirm={async () =>
              await handleDeleteBankAccount(selectedBankAccountId)
            }
            onCancel={() => setIsModalOpen(false)} />
        )}
      </div>
    )
  }

  return (


    <div className="space-y-4">
      {content}
    </div>
  );
}
